import { useEffect, useMemo, useState } from "react";
import type { HostContext, ShellRuntime } from "@shared/host/shell-runtime";

export interface UseShellBootstrapOptions {
  graceTimeoutMs?: number;
  hostContext?: HostContext;
  isFederated: () => boolean;
  isStandalone: () => boolean;
  readHostSnapshot: () => HostContext | undefined;
  readShellRuntime: () => ShellRuntime | undefined;
}

export interface ShellBootstrapState {
  isInsideShell: boolean;
  isWaitingForHostContext: boolean;
  resolvedHostContext?: HostContext;
  shellRuntime?: ShellRuntime;
  standaloneMode: boolean;
}

const DEFAULT_GRACE_TIMEOUT_MS = 500;

export const useShellBootstrap = ({
  graceTimeoutMs = DEFAULT_GRACE_TIMEOUT_MS,
  hostContext,
  isFederated,
  isStandalone,
  readHostSnapshot,
  readShellRuntime,
}: UseShellBootstrapOptions): ShellBootstrapState => {
  const standaloneMode = isStandalone();
  const shellRuntime = readShellRuntime();
  const resolvedHostContext = useMemo(
    () => hostContext ?? readHostSnapshot(),
    [hostContext, readHostSnapshot],
  );
  const isInsideShell =
    !standaloneMode &&
    (
      isFederated() ||
      Boolean(hostContext) ||
      Boolean(shellRuntime)
    );
  const [isWaitingForHostContext, setIsWaitingForHostContext] = useState(
    isInsideShell && !resolvedHostContext,
  );

  useEffect(() => {
    if (!isInsideShell) {
      setIsWaitingForHostContext(false);
      return;
    }

    if (resolvedHostContext) {
      setIsWaitingForHostContext(false);
      return;
    }

    const graceTimeoutId = window.setTimeout(() => {
      setIsWaitingForHostContext(false);
    }, graceTimeoutMs);

    return () => {
      window.clearTimeout(graceTimeoutId);
    };
  }, [graceTimeoutMs, isInsideShell, resolvedHostContext]);

  return {
    isInsideShell,
    isWaitingForHostContext,
    resolvedHostContext,
    shellRuntime,
    standaloneMode,
  };
};
